import { NumberField } from "@/components/inputs";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { TextField, MenuItem } from "@mui/material";
import { useEffect, useState } from "react";

export interface MonedaOperacionFieldProps {
  value?: { monedaOperacionId: string; conversionGuarani: number };
  onChange?: (val: { monedaOperacionId: string; conversionGuarani: number }) => void;
  forAutofactura?: boolean;
}

export function MonedaOperacionField(
  {
    value = { monedaOperacionId: "", conversionGuarani: 1 },
    onChange,
    forAutofactura = false,
  }: MonedaOperacionFieldProps) {

  const { monedas, monedaBase, cotizacion } = useMonetizacion();

  const monedaGuarani = monedas.find(m => m.simbolo === 'PYG');
  const defaultMoneda = forAutofactura ? (monedaGuarani?.id || monedaBase.id) : (value.monedaOperacionId || monedaBase.id);
  
  const [monedaOperacion, setMonedaOperacion] = useState(defaultMoneda);
  const [conversionGuarani, setConversionGuarani] = useState(value.conversionGuarani ?? 1);

  const isMonedaBaseGuarani = monedaBase.simbolo === 'PYG';
  const monedaSeleccionada = monedas.find(m => m.id === monedaOperacion);
  const monedaParaCotizar = monedaSeleccionada?.id;

  const cotizItem = cotizacion?.cotizacionItems?.find((c: any) => c.monedaId === monedaParaCotizar);
  const cotizValue = cotizItem ? cotizItem.monedaBaseRelacion : 1;

  const isOperacionGuarani = monedaSeleccionada?.simbolo === 'PYG' && isMonedaBaseGuarani;

  const getLabel = () => {
    if (isMonedaBaseGuarani) {
      return `Cambio a Guaraníes (PYG)`;
    } else {
      if (monedaSeleccionada?.simbolo === 'PYG') {
        return `${monedaBase.nombre} (${monedaBase.simbolo}) a Guaraníes (PYG)`;
      } else {
        return `${monedaSeleccionada?.nombre} (${monedaSeleccionada?.simbolo}) a Guaraníes (PYG)`;
      }
    }
  }

  // Calcular conversión a Guaraníes cada vez que cambie la moneda de operación o la cotización
  useEffect(() => {
    if (monedaBase.simbolo === 'PYG') {
      setConversionGuarani(cotizValue);

    } else {
      if (monedaSeleccionada?.simbolo === 'PYG' || monedaBase.simbolo === monedaSeleccionada?.simbolo) {
        //El cambio a guaranies se toma de la cotización de la moneda seleccionada o de la moneda base
        let cambio = cotizacion.cotizacionItems.find((c: any) => c.monedaId === monedaGuarani?.id)?.monedaBaseRelacion ?? 1;
        setConversionGuarani(cambio);

      } else {
        //No se tiene el cambio a Guaranies
        let cambio = 1;
        setConversionGuarani(cambio);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cotizValue, monedaOperacion]);

  // Notificar cambios al padre
  useEffect(() => {
    onChange?.({ monedaOperacionId: monedaOperacion, conversionGuarani });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monedaOperacion, conversionGuarani]);

  const handleMonedaChange = (id: string) => {
    setMonedaOperacion(id);
  };

  // Filtrar monedas si es para autofactura
  const monedasDisponibles = forAutofactura
    ? monedas.filter(m => m.simbolo === 'PYG')
    : monedas;

  return (
    <>
      <TextField
        select
        label="Moneda de Operación"
        value={monedaOperacion}
        onChange={e => handleMonedaChange(e.target.value)}
        size="small"
        sx={{ minWidth: 180 }}
      >
        {monedasDisponibles.map(moneda => (
          <MenuItem key={moneda.id} value={moneda.id}>
            {moneda.nombre} ({moneda.simbolo})
          </MenuItem>
        ))}
      </TextField>
      {!isOperacionGuarani && (
        <NumberField
          decimalScale={2}
          label={getLabel()}
          value={(conversionGuarani !== 0 && !isMonedaBaseGuarani) ? 1 / conversionGuarani : conversionGuarani}
          onValueChange={(values) => {
            const inputValue = values.floatValue ?? 0;
            const directValue = (inputValue !== 0 && !isMonedaBaseGuarani) ? (1 / inputValue) : inputValue;
            setConversionGuarani(directValue);
          }}
          size="small"
          sx={{ minWidth: 180 }}
          inputProps={{ step: "any" }}
        />
      )}
    </>
  );
}
